import { Suspense } from "react";

import {
  Badge,
  Column,
  ProjectsIcon,
  Skeleton,
  SkeletonBox,
} from "@hightouch/htui";

import { navIcon } from "src/components/layout/nav/nav-icon";
import { NavItem } from "src/components/layout/nav/nav-item";
import {
  loadProjectsNavLinks,
  useCanUseProductTabs,
  useProjectsAccess,
} from "src/components/layout/nav/sections/hooks/projects";
import {
  PROJECTS_AREA_SEGMENTS,
  PROJECTS_PATHS,
  PROJECTS_TERMS as TERMS,
} from "src/pages/projects/constants";
import {
  formatUnreadCount,
  type UnreadCount,
} from "src/pages/projects/inbox/inbox-queries";
import { lazyNamed } from "src/utils/lazy-named";

const ProjectsNavLinks = lazyNamed(loadProjectsNavLinks, "ProjectsNavLinks");

const ProjectsNavIcon = navIcon(ProjectsIcon);
const AREA_PATHS = PROJECTS_AREA_SEGMENTS.map((segment) => `/${segment}`);

/** The classic navigation's entry to the area, for AMP users who switched back from the product tabs. */
export const Projects = () => {
  const canUseProductTabs = useCanUseProductTabs();

  if (!canUseProductTabs) {
    return null;
  }

  return (
    <NavItem
      href={PROJECTS_PATHS.projects}
      activePaths={AREA_PATHS}
      icon={ProjectsNavIcon}
      label={TERMS.area}
      // Starts the area's nav loading before the click, so it doesn't open on an empty column.
      onMouseOver={() => void loadProjectsNavLinks()}
      onFocus={() => void loadProjectsNavLinks()}
    />
  );
};

/** The viewer's unread inbox notifications, or nothing when there are none. */
export const UnreadBadge = ({ unread }: { unread: UnreadCount }) =>
  unread.count > 0 ? (
    <Badge size="sm" variant="info">
      {formatUnreadCount(unread)}
    </Badge>
  ) : null;

// The area's fixed links, at the nav's row height and gap.
const NAV_SKELETON = (
  <Skeleton isLoading>
    <Column gap={3}>
      {[0, 1].map((row) => (
        <SkeletonBox key={row} height={8} />
      ))}
    </Column>
  </Skeleton>
);

export const ProjectsNav = () => {
  const { isEnabled } = useProjectsAccess();

  // The list query waits until access is known to be granted.
  return isEnabled ? (
    <Suspense fallback={NAV_SKELETON}>
      <ProjectsNavLinks />
    </Suspense>
  ) : (
    NAV_SKELETON
  );
};
